import { z } from 'zod';

const SplitDtoSchema = z.object({
  user_id: z.string(),
  user_name: z.string(),
  share_minor: z.number().int().min(0),
});

export const ExpenseDtoSchema = z.object({
  id: z.string(),
  group_id: z.string(),
  description: z.string(),
  amount_minor: z.number().int().min(0), // clamp: amounts are never negative
  currency: z.string().length(3),
  paid_by: z.object({ id: z.string(), name: z.string() }),
  date: z.string(),
  splits: z.array(SplitDtoSchema),
});

export const ExpenseListDtoSchema = z.object({
  items: z.array(ExpenseDtoSchema),
});

export type ExpenseDto = z.infer<typeof ExpenseDtoSchema>;

/* ----------------------------------------------------------- Real backend API ---------- */

/** A per-member split from the real `GET /api/v1/expense/{groupId}` (amounts in MINOR units). */
const ApiSplitSchema = z.object({
  user_id: z.string(),
  expense_amount: z.number().default(0),
  group_expense_amount: z.number().default(0),
  // When absent, treat the member as NOT part of the split — a member the backend never explicitly
  // marked `true` for shouldn't silently appear in the read-only Split-share view (they showed up
  // there with a $0.00 share, since a missing field defaulting to `true` passed the `splitEnabled`
  // inclusion filter downstream).
  split_enabled: z.boolean().default(false),
  // Raw per-mode input the split was entered with (percentage / shares / amount) — used, together
  // with the expense-level `split_type`, to prefill the edit form's Split tab + values.
  value: z.number().default(0),
});

/** A payment leg — who fronted the money and how much (MINOR units). A WALLET leg carries its id
 *  on `wallet_id` (its `user_id` comes back null); member legs use `user_id`. */
const ApiPaymentSchema = z.object({
  type: z.string().nullish(),
  user_id: z.string().nullish(),
  wallet_id: z.string().nullish(),
  paid_amount: z.number().default(0),
  group_paid_amount: z.number().default(0),
});

/** Expense category — `name` label + a base64/URL `image` for the row icon. */
const ApiExpenseTypeSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
  image: z.string().nullish(),
  color: z.string().nullish(),
});

/** Currency descriptor carried on the expense (`currency` code + display `symbol`). */
const ApiCurrencySchema = z.object({
  currency: z.string().nullish(),
  symbol: z.string().nullish(),
});

/** One breakdown item from the expense `items[]` array (`sing` = `+`/`-`, amount in MINOR units). */
const ApiBreakdownItemSchema = z.object({
  _id: z.string().nullish(),
  name: z.string().default(''),
  amount: z.number().default(0),
  type: z.string().nullish(),
  sing: z.string().nullish(), // `sing` (sic) — backend field name, means sign
});

/** One expense as returned by the real backend (snake_case). Only consumed fields are declared. */
export const ApiExpenseDtoSchema = z.object({
  _id: z.string(),
  group_id: z.string(),
  user_id: z.string().nullish(),
  description: z.string().default(''),
  notes: z.string().nullish(),
  date: z.string(),
  created_at: z.string().nullish(),
  is_private: z.boolean().default(false),
  split_type: z.string().nullish(),
  group_currency_amount: z.number().default(0),
  expense_amount: z.number().default(0),
  group_currency: ApiCurrencySchema.nullish(),
  expense_currency: ApiCurrencySchema.nullish(),
  expense_type: ApiExpenseTypeSchema.nullish(),
  is_currency_converted: z.boolean().default(false),
  exchange_rate: z.number().nullish(),
  exchange_rate_date: z.string().nullish(),
  // Shapes for recurring rules / attachment vary across the API — read defensively in the mapper.
  recurring_rules: z.unknown().nullish(),
  attachment: z.unknown().nullish(),
  splits: z.array(ApiSplitSchema).default([]),
  payments: z.array(ApiPaymentSchema).default([]),
  items: z.array(ApiBreakdownItemSchema).default([]),
  // Location — sent as strings on the wire (create/update payload), so accept string OR number on
  // read too (defensive; the mapper coerces). Absent/empty ⇒ no location was attached.
  location: z.string().nullish(),
  location_lat: z.union([z.string(), z.number()]).nullish(),
  location_long: z.union([z.string(), z.number()]).nullish(),
  location_address1: z.string().nullish(),
  location_address2: z.string().nullish(),
  location_city: z.string().nullish(),
  location_state: z.string().nullish(),
  location_zip: z.string().nullish(),
  location_country: z.string().nullish(),
});

export type ApiExpenseDto = z.infer<typeof ApiExpenseDtoSchema>;

/* ----------------------------------------------- Journal-entry transactions feed ------- */

/** A participant resolved on a transaction (names + avatar live here, not on the expense). */
const TransactionUserSchema = z.object({
  user_id: z.string(),
  display_name: z.string().nullish(),
  full_name: z.string().nullish(),
  email: z.string().nullish(),
  phone_number: z.string().nullish(),
  profile_picture_url: z.string().nullish(),
  is_guest: z.boolean().default(false),
});

/** A person-to-person settlement: `paid_from` pays `paid_to` (`amount` in group-currency minor). */
const SettleDtoSchema = z.object({
  _id: z.string().nullish(),
  paid_from: z.string().nullish(),
  paid_to: z.string().nullish(),
  amount: z.number().default(0),
  currency_symbol: z.string().nullish(),
  notes: z.string().nullish(),
  date: z.string().nullish(),
  created_at: z.string().nullish(),
  created_by_user_id: z.string().nullish(),
});

/** A wallet top-up: `deposit_from` adds `amount` to the shared wallet. */
const DepositDtoSchema = z.object({
  record_id: z.string().nullish(),
  date: z.string().nullish(),
  amount: z.number().default(0),
  currency_id: z.string().nullish(),
  currency_symbol: z.string().nullish(),
  description: z.string().nullish(),
  deposit_from: z.string().nullish(),
  created_by_user_id: z.string().nullish(),
  created_at: z.string().nullish(),
  exchange_rate: z.number().nullish(),
  exchange_rate_source: z.string().nullish(),
  exchange_rate_date: z.string().nullish(),
  selected_currency_id: z.string().nullish(),
  selected_currency_amount: z.number().nullish(),
});

/** A wallet-balance closure: the remaining `amount` is transferred to `transfer_to_user_id`. */
const WalletClosureDtoSchema = z.object({
  record_id: z.string().nullish(),
  amount: z.number().default(0),
  is_final_transfer: z.boolean().nullish(),
  transfer_to_user_id: z.string().nullish(),
  created_by_user_id: z.string().nullish(),
  description: z.string().nullish(),
  date: z.string().nullish(),
  created_at: z.string().nullish(),
});

/**
 * One row from `GET /api/v1/expense/{groupId}/new` — the accounting "journal entry" feed. A
 * `reference_type` discriminates the payload: `EXPENSE` → `expense`, `SETTLEMENT` → `settle`,
 * `WALLET_TOPUP` → `deposit`, `WALLET_CLOSE_TRANSFER` → `wallet_balance_closure`. `users` resolves
 * the row's participant names + avatars.
 */
export const TransactionDtoSchema = z.object({
  date: z.string().nullish(),
  reference_type: z.string().nullish(),
  created_at: z.string().nullish(),
  record_id: z.string().nullish(),
  expense: ApiExpenseDtoSchema.nullish(),
  settle: SettleDtoSchema.nullish(),
  deposit: DepositDtoSchema.nullish(),
  wallet_balance_closure: WalletClosureDtoSchema.nullish(),
  users: z.array(TransactionUserSchema).default([]),
});

/** Envelope of `GET /api/v1/expense/{groupId}/new`: `{ status, message, data: { transactions } }`. */
export const TransactionListResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().optional(),
  data: z
    .object({
      transactions: z.array(TransactionDtoSchema).default([]),
      deleted: z.array(z.string()).nullish(),
    })
    .default({ transactions: [] }),
});

export type TransactionDto = z.infer<typeof TransactionDtoSchema>;
export type TransactionUserDto = z.infer<typeof TransactionUserSchema>;

/* ------------------------------------------- pending-expense create/update response ---- */

/**
 * Envelope of `POST`/`PATCH /api/v1/pending-expense`: `{ status, message, data: { expense,
 * show_profile_popup } }`. `data`/`expense` are nullish-tolerant — we read only the id + popup flag
 * (the full created expense is re-fetched via the list/ledger queries). `status === false` is a
 * business failure surfaced by the command, not a parse error.
 */
export const PendingExpenseSaveResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().nullish(),
  data: z
    .object({
      expense: z.object({ _id: z.string().nullish(), id: z.string().nullish() }).nullish(),
      show_profile_popup: z.boolean().nullish(),
    })
    .nullish(),
});

export type PendingExpenseSaveResponse = z.infer<typeof PendingExpenseSaveResponseSchema>;

/**
 * Envelope of `POST /api/v1/pending-expense/{id}/attachment/upload` (multipart, single `file_url`
 * part). We only need the returned `file_url`; `data` is nullish-tolerant.
 */
export const PendingExpenseAttachmentUploadResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().nullish(),
  data: z.object({ file_url: z.string().nullish() }).nullish(),
});

/** Envelope of `DELETE /api/v1/expense/{groupId}/{expenseId}` — `status`/`message` only. A
 *  `status === false` is a business failure (the message is safe to surface). */
export const ExpenseDeleteResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().nullish(),
});

export type PendingExpenseAttachmentUploadResponse = z.infer<
  typeof PendingExpenseAttachmentUploadResponseSchema
>;

/**
 * Envelope of `GET /api/v1/expense/pending/{expenseId}/attachment` (API 5, view attachment):
 * `data { base64, content_type, size }`. All nullish-tolerant; empty `base64` ⇒ no attachment.
 */
export const PendingExpenseAttachmentViewResponseSchema = z.object({
  status: z.boolean().optional(),
  message: z.string().nullish(),
  data: z
    .object({
      base64: z.string().nullish(),
      content_type: z.string().nullish(),
      size: z.number().nullish(),
    })
    .nullish(),
});

export type PendingExpenseAttachmentViewResponse = z.infer<
  typeof PendingExpenseAttachmentViewResponseSchema
>;
